import { Eye, EyeOff } from "lucide-react";

import { CodePanel } from "@/components/guide/code-panel";
import type { Chapter } from "@/components/guide/tutorial";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { maskKey } from "@/lib/auth";

/**
 * Chapter 1: a working environment. Two commands and a two-line file. The operating system
 * changes only the first command; the reader's own key is filled into the file once they are
 * signed in -- masked on screen, real on the clipboard.
 */

export type Os = "unix" | "windows";

export interface SetupState {
  os: Os;
  setOs: (os: Os) => void;
  apiKey: string | null;
  keyShown: boolean;
  setKeyShown: (shown: boolean) => void;
}

const INSTALL: Record<Os, string> = {
  unix: "curl -LsSf https://astral.sh/uv/install.sh | sh",
  windows: "irm https://astral.sh/uv/install.ps1 | iex",
};
const CLONE = "git clone https://github.com/ai4ce/vis_nav_player.git\ncd vis_nav_player\nuv sync";

export function setupChapter(state: SetupState): Chapter {
  const { os, setOs, apiKey, keyShown, setKeyShown } = state;
  const real = apiKey ?? "<your key>";
  const shown = apiKey && !keyShown ? maskKey(apiKey) : real;
  const windows = os === "windows";
  const dotenv = (key: string) => `VIS_NAV_API_KEY=${key}\nVIS_NAV_CHALLENGE=<challenge id>`;

  return {
    id: "setup",
    label: "Setup",
    title: "Environment setup",
    intro: "Two commands and a two-line file. Only uv is installed globally; nothing goes into your shell configuration.",
    controls: (
      <Tabs value={os} onValueChange={(value) => setOs(value as Os)}>
        <TabsList aria-label="Operating system">
          <TabsTrigger value="unix">macOS / Linux</TabsTrigger>
          <TabsTrigger value="windows">Windows</TabsTrigger>
        </TabsList>
      </Tabs>
    ),
    steps: [
      {
        title: "Install uv",
        body: (
          <>
            uv is a Python package manager that also installs Python. The kit pins Python 3.12 and
            every dependency, so everyone gets the same environment. No conda, no virtualenv to
            activate.
            {windows ? " Use PowerShell, not WSL: the viewer window works better natively." : ""}
          </>
        ),
      },
      {
        title: "Get the kit",
        body: (
          <>
            The starter kit has a keyboard agent, a baseline, and the skeleton your agent goes in.
            Open a new terminal, clone it, and run <code className="font-mono text-xs">uv sync</code>.
            That creates <code className="font-mono text-xs">.venv</code> and installs everything,
            including the course SDK. From then on <code className="font-mono text-xs">uv run</code>{" "}
            runs a script inside it.
          </>
        ),
      },
      {
        title: "Create .env",
        body: (
          <>
            The scripts need your API key and the id of the challenge you are working on. Put both
            in a file called <code className="font-mono text-xs">.env</code> in the kit folder. Every
            script reads it, and git ignores it, so the key never ends up in a commit.{" "}
            {apiKey
              ? "You are signed in, so your key is filled in below, masked on screen and real when copied."
              : "Sign in at the top right and your key is filled in below."}{" "}
            The challenge id is the last part of a challenge page&apos;s URL,{" "}
            <code className="font-mono text-xs">/challenges/&lt;id&gt;</code>. Start with a practice
            challenge.
          </>
        ),
      },
    ],
    preview: (step) =>
      step < 2 ? (
        <CodePanel
          chunks={[
            { from: 0, code: INSTALL[os] },
            { from: 1, code: `\n# in a new terminal:\n${CLONE}` },
          ]}
          step={step}
          language="bash"
          title={windows ? "PowerShell" : "Terminal"}
        />
      ) : (
        <CodePanel
          chunks={[{ from: 0, code: dotenv(shown) }]}
          step={0}
          language="bash"
          title="vis_nav_player/.env"
          copy={() => dotenv(real)}
          action={
            apiKey ? (
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                onClick={() => setKeyShown(!keyShown)}
                aria-label={keyShown ? "Hide key" : "Show key"}
                aria-pressed={keyShown}
              >
                {keyShown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
              </Button>
            ) : null
          }
        />
      ),
  };
}
